import React, { Component } from 'react'
import { graphql } from 'gatsby'
import { Columns, Column, Container, Content, Hero, HeroBody, Subtitle, Title } from 'bloomer'
import styled from 'styled-components'

// import Breadcrumbs from '../../components/breadcrumbs'
import CallsToAction from '../../components/ctas'
import GetStarted from '../../components/start'
import Head from '../../components/head'
import Image from '../../components/image'
import Layout from '../../components/layout'
import Next from '../../components/next'

import workflowFeatures from '../../content/features/workflow-management'

const FeatureHighlight = styled.div`
  align-items: center;
  display: flex;
  flex-flow: column nowrap;
  height: 25rem;
  justify-content: center;
  width: 100%;

  @media only screen and (min-width: 32rem) {
    display: block;
  }
`

const ShortHeroBody = styled(HeroBody)`
  padding-top: 3rem !important;

  @media only screen and (min-width: 45rem) {
    padding-top: 9rem !important;
  }
`

const HeroImage = styled(Image)`
  margin: 0 auto -24rem auto;

  @media only screen and (min-width: 45rem) {
    height: 60%;
    width: 60%;
  }

  @media only screen and (min-width: 90rem) {
    height: 33%;
    width: 33%;
  }
`

const MockupImage = styled(Image)`
  margin: 0 auto;
  z-index: 1;

  @media only screen and (min-width: 45rem) {
    height: 75%;
    width: 75%;
  }
`

const TopMarginContainer = styled(Container)`
  margin-top: 24rem !important;
`

class WorkflowManagement extends Component {
  render() {
    const featureHighlights = workflowFeatures.map((feature, i) => (
      <Column key={i} isSize={{ default: 3, mobile: 'full' }}>
        <FeatureHighlight>
          <Image alt={feature.title} file={this.props.data[feature.icon]} />
          <Subtitle isSize={4} style={{ fontWeight: 700, margin: '2rem 0' }}>
            {feature.title}
          </Subtitle>
          <Content isSize={'medium'} style={{ margin: '0 1rem' }}>
            {feature.description}
          </Content>
        </FeatureHighlight>
      </Column>
    ))

    let HeroSection = (
      <Hero isColor={'light'} isSize={'medium'}>
        <ShortHeroBody>
          <Container hasTextAlign={'right'}>
            <Title isSize={1} className={'huge'}>
              {`Workflow Management`}
            </Title>
            <Subtitle hasTextAlign={'right'} isSize={3}>
              {`Achieve Service Excellence From Request to Response With Offline
              Enabled Task Management.`}
            </Subtitle>
            <CallsToAction />
          </Container>
        </ShortHeroBody>
      </Hero>
    )

    let FeatureHighlightsSection = (
      <Hero isColor={'black'} isSize={'medium'}>
        <HeroBody>
          <TopMarginContainer hasTextAlign={'centered'}>
            <Subtitle isSize={1}>{`Core Workflow Management Features`}</Subtitle>
            <Content isSize={'large'} style={{ fontWeight: 300, lineHeight: 1.8, marginBottom: '5rem' }}>
              {`Not every service comes with a long term contract. For some
              providers, particularly those heavily focused in special service
              segments or maintenance work, these services can be the lifeblood
              of your business. For others, Requests Tickets are commonplace
              while servicing larger Clients. Managing Workflows with Route
              enhances team performance in the field while providing management
              with the visibility and data they crave.`}
            </Content>
            <Columns hasTextAlign={'centered'} isGrid style={{ justifyContent: 'center', marginTop: '4rem' }}>
              {featureHighlights}
            </Columns>
          </TopMarginContainer>
        </HeroBody>
      </Hero>
    )

    let OnTheJobSection = (
      <Hero isColor={'black'} isSize={'medium'}>
        <HeroBody>
          <Container>
            <Subtitle isSize={1}>{`Redefine Work In the Field`}</Subtitle>
            <Subtitle isSize={3}>
              {`Become a data driven Service Company with real time updates from
              the job site.`}
            </Subtitle>
            <Content isSize={'medium'} style={{ fontWeight: 300, lineHeight: 1.8 }}>
              {`Assign work and track progress from any computer. Your team can
              view the job info out in the field from their phones, and with one
              touch task completions, collecting real time data on site about
              performance is seamless.`}
            </Content>{' '}
            <Content isSize={'medium'} style={{ fontWeight: 300, lineHeight: 1.8 }}>
              {`Dive into the details and uncover important insights from your
              entire business like never before while holding teams to a new
              level of accountability.`}
            </Content>
            <Columns isGrid>
              <Column isSize={{ default: 6, mobile: 'full' }}>
                <MockupImage
                  alt={'Schedule of Walkthroughs'}
                  file={this.props.data.schedule}
                  style={{ height: '100%' }}
                />
              </Column>
              <Column isSize={{ default: 6, mobile: 'full' }} hasTextAlign={'right'}>
                <ul
                  style={{
                    display: 'flex',
                    flexFlow: 'column nowrap',
                    height: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <li>
                    <Subtitle isSize={4}>{`SCHEDULING DONE RIGHT`}</Subtitle>
                    <Content isSize={'medium'} style={{ marginTop: '2rem' }}>
                      {`Schedule and assign team members, then set tasks for jobs
                      with a few clicks. Share the information with the Route
                      app.`}
                    </Content>
                  </li>
                  <li>
                    <Subtitle isSize={4} style={{ marginTop: '2rem' }}>
                      {`COMPLETE JOB TRANSPARENCY`}
                    </Subtitle>
                    <Content isSize={'medium'} style={{ marginTop: '2rem' }}>
                      {`No more confusion about jobs. Assign your team, create
                      custom tasks tailored for the job, and track clock ins and
                      clock outs.`}
                    </Content>
                  </li>
                  <li>
                    <Subtitle isSize={4} style={{ marginTop: '2rem' }}>
                      {`REVIEW JOB DETAILS`}
                    </Subtitle>
                    <Content isSize={'medium'} style={{ marginTop: '2rem', marginBottom: '0.75rem' }}>
                      {`Check the status of jobs anywhere and anytime.
                      Confirmation of completed work at glance with photos,
                      timestamps, and notes.`}
                    </Content>
                  </li>
                </ul>
              </Column>
            </Columns>
          </Container>
        </HeroBody>
      </Hero>
    )

    let TaskManagementSection = (
      <Hero isColor={'light'} isSize={'medium'}>
        <HeroBody>
          <Container>
            <Columns isGrid>
              <Column>
                <Subtitle isSize={1} style={{ marginTop: '4rem' }}>
                  {`Manage Workflows From Areas to Tasks`}
                </Subtitle>
                <Subtitle isSize={4}>
                  {`Dynamic Workflows any Team can follow without changing their
              underlying process in the field.`}
                </Subtitle>
                <Content isSize={'medium'}>
                  {`Designed to fit the needs of any team, Route Workflows provide
              teams with everything they need to get the job done their way.`}
                </Content>
                <Content isSize={'medium'}>
                  {`Use Areas to gather data about Rooms - or groups of them - such as
              dimensions, floor type, and hot points from your Client's space in
              the field.`}
                </Content>
                <Content isSize={'medium'}>
                  {`Tasks are perfect for defining the Scope of Work, and in tandem
              with Areas create a picture perfect representation of the job at
              hand for your Technicians.`}
                </Content>
              </Column>
              <Column>
                <MockupImage
                  alt={'workflows for task management'}
                  file={this.props.data.workflows}
                  style={{ height: '100%' }}
                />
              </Column>
            </Columns>
          </Container>
        </HeroBody>
      </Hero>
    )

    let PerformanceSection = (
      <Hero isColor={'black'} isSize={'medium'}>
        <HeroBody>
          <Container>
            <Subtitle isSize={1}>{`Great Performance Calls For Great Metrics.`}</Subtitle>
            <Subtitle isSize={3}>
              {`Workflows record everything from completion times to calories
              burned, empowering teams to take their service to the next level.`}
            </Subtitle>
            <Columns isGrid>
              <Column isSize={{ default: 6, mobile: 'full' }}>
                <MockupImage
                  alt={'Route technician profile screen'}
                  file={this.props.data.profile}
                  style={{ height: '100%' }}
                />
              </Column>
              <Column isSize={{ default: 6, mobile: 'full' }} hasTextAlign={'right'}>
                <ul>
                  <li style={{ marginTop: 0 }}>
                    <Subtitle isSize={3} style={{ margin: 0 }}>
                      {`TASK COMPLETION TIMES`}
                    </Subtitle>
                    <Content isSize={'large'}>
                      {`The black hole has subsided. Task completion times are
                      recorded on every swipe, even without internet.`}
                    </Content>
                  </li>
                  <li style={{ marginTop: '1rem' }}>
                    <Subtitle isSize={3} style={{ margin: 0 }}>
                      {`HEALTH AND FITNESS`}
                    </Subtitle>
                    <Content isSize={'large'}>
                      {`Building Services are a great workout. Route keeps track
                      of steps taken, distance traveled, and calories burned.`}
                    </Content>
                  </li>
                  <li style={{ marginTop: '1rem' }}>
                    <Subtitle isSize={3} style={{ margin: 0 }}>
                      {`TECHNICIAN PROFILES`}
                    </Subtitle>
                    <Content isSize={'large'}>
                      {`Recognize outstanding performance across the organization
                      with gamified team profiles that reward their big
                      accomplishments.`}
                    </Content>
                  </li>
                </ul>
              </Column>
            </Columns>
          </Container>
        </HeroBody>
      </Hero>
    )

    return (
      <Layout>
        <Head
          author={'Route'}
          title={'Workflow Managements: Perform Work With Route'}
          description={`The Route Platform's Workflow Management Toolkit has everything your team needs to get the job done.`}
          keywords={[
            'Route',
            'workflow management',
            'workflow manager',
            'task management',
            'task manager',
            'service manager',
            'SaaS',
            'software for service companies'
          ]}
          {...this.props}
        />
        {/* <Breadcrumbs {...this.props} /> */}
        {HeroSection}
        <HeroImage alt={'Route Mobile Workflow Management for Building Services'} file={this.props.data.hero} />
        {FeatureHighlightsSection}
        {OnTheJobSection}
        {TaskManagementSection}
        {PerformanceSection}
        <Next
          style={{ marginTop: '3.5rem' }}
          to={'/features/inspection-reports'}
          title={'Next Up: Inspection Reports'}
        />
        <GetStarted />
      </Layout>
    )
  }
}

export const query = graphql`
  query WorkflowManagementQuery {
    hero: file(relativePath: { eq: "assets/mockups/mobile/clocked-in-job-2.png" }) {
      ...FluidImage
    }
    schedule: file(relativePath: { eq: "assets/mockups/mobile/schedule-1.png" }) {
      ...FluidImage
    }
    profile: file(relativePath: { eq: "assets/mockups/mobile/employee-profile.png" }) {
      ...FluidImage
    }
    workflows: file(relativePath: { eq: "assets/mockups/mobile/clocked-in-job-1.png" }) {
      ...FluidImage
    }
    offlineFirst: file(relativePath: { eq: "assets/icons/flaticon/offline-first.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    taskManagement: file(relativePath: { eq: "assets/icons/flaticon/task-management.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    teamShifts: file(relativePath: { eq: "assets/icons/flaticon/team-shifts.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    realtimeSupport: file(relativePath: { eq: "assets/icons/flaticon/real-time-support.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`

export default WorkflowManagement
